// src/utils/notificationService.js - AI強化版（完全置き換え）
import { collection, addDoc, doc, updateDoc, deleteDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

let aiModel = null;

// AI初期化（遅延読み込み）
const initializeAI = async () => {
  if (!aiModel && process.env.REACT_APP_GEMINI_API_KEY) {
    try {
      const { GoogleGenerativeAI } = await import("@google/generative-ai");
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
      aiModel = genAI.getGenerativeModel({ model: "gemini-1.5-flash" });
      console.log('🤖 AI機能初期化完了');
    } catch (error) {
      console.warn('AI機能の初期化に失敗:', error);
    }
  }
  return aiModel;
};

export class NotificationService {
  // WebSocket接続管理
  static activeConnections = new Map();
  static aiCache = new Map();

  // ========== 基本通知機能（既存互換） ==========

  // 通知を作成
  static async createNotification({
    recipientId,
    title,
    message,
    type = 'system',
    actionUrl = null,
    priority = 'normal',
    senderId = null,
    relatedData = null,
    aiEnhanced = false  // AI強化フラグ
  }) {
    try {
      let enhancedMessage = message;
      let aiAnalysis = null;
      let recommendedActions = [];

      // AI分析（設定されている場合のみ）
      if (aiEnhanced) {
        const ai = await initializeAI();
        if (ai) {
          const analysis = await this.analyzeWithAI(title, message, type, relatedData);
          enhancedMessage = analysis.enhancedMessage || message;
          aiAnalysis = analysis.analysis;
          recommendedActions = analysis.recommendedActions || [];
        }
      }

      const notification = {
        recipientId,
        title,
        message: enhancedMessage,
        originalMessage: message,
        type,
        actionUrl,
        priority,
        senderId,
        relatedData,
        isRead: false,
        createdAt: new Date(),
        readAt: null,
        // AI強化フィールド
        aiEnhanced,
        aiAnalysis,
        recommendedActions,
        urgencyScore: this.calculateUrgencyScore(priority, type)
      };

      const docRef = await addDoc(collection(db, "notifications"), notification);
      
      // WebSocket配信（接続がある場合）
      this.broadcastToWebSocket(notification, docRef.id);
      
      console.log("通知を作成しました:", docRef.id);
      return docRef.id;
    } catch (error) {
      console.error("通知作成エラー:", error);
      throw error;
    }
  }

  // 複数人に一括通知
  static async createBulkNotification({
    recipientIds,
    title,
    message,
    type = 'system',
    actionUrl = null,
    priority = 'normal',
    senderId = null,
    relatedData = null,
    aiEnhanced = false
  }) {
    try {
      const promises = recipientIds.map(recipientId => 
        this.createNotification({
          recipientId,
          title,
          message,
          type,
          actionUrl,
          priority,
          senderId,
          relatedData,
          aiEnhanced
        })
      );

      const results = await Promise.all(promises);
      console.log(`${results.length}件の通知を作成しました`);
      return results;
    } catch (error) {
      console.error("一括通知作成エラー:", error);
      throw error;
    }
  }

  // 全スタッフに通知
  static async createGlobalNotification({
    title,
    message,
    type = 'system',
    actionUrl = null,
    priority = 'normal',
    senderId = null,
    relatedData = null,
    aiEnhanced = false
  }) {
    try {
      const staffSnapshot = await getDocs(collection(db, "staffList"));
      const recipientIds = staffSnapshot.docs.map(doc => doc.data().employeeId);

      return await this.createBulkNotification({
        recipientIds,
        title,
        message,
        type,
        actionUrl,
        priority,
        senderId,
        relatedData,
        aiEnhanced
      });
    } catch (error) {
      console.error("全体通知作成エラー:", error);
      throw error;
    }
  }

  // 特定の条件に合うスタッフに通知
  static async createConditionalNotification({
    condition,
    title,
    message,
    type = 'system',
    actionUrl = null,
    priority = 'normal',
    senderId = null,
    relatedData = null,
    aiEnhanced = false
  }) {
    try {
      const staffSnapshot = await getDocs(collection(db, "staffList"));
      const targetStaff = staffSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(condition);

      const recipientIds = targetStaff.map(staff => staff.employeeId);

      return await this.createBulkNotification({
        recipientIds,
        title,
        message,
        type,
        actionUrl,
        priority,
        senderId,
        relatedData,
        aiEnhanced
      });
    } catch (error) {
      console.error("条件付き通知作成エラー:", error);
      throw error;
    }
  }

  // ========== 業務固有の通知作成メソッド（既存互換） ==========

  // シフト変更通知
  static async notifyShiftChange(recipientId, changes, senderId) {
    return await this.createNotification({
      recipientId,
      title: "シフト変更のお知らせ",
      message: `あなたのシフトが変更されました。詳細: ${changes}`,
      type: "shift",
      actionUrl: "/calendar",
      priority: "high",
      senderId,
      relatedData: { changes },
      aiEnhanced: true  // AI強化を有効化
    });
  }

  // 手術配置変更通知
  static async notifyAssignmentChange(recipientId, surgeryInfo, senderId) {
    return await this.createNotification({
      recipientId,
      title: "手術配置変更",
      message: `${surgeryInfo.date} ${surgeryInfo.procedure}の配置が変更されました`,
      type: "assignment",
      actionUrl: "/assignments",
      priority: "high",
      senderId,
      relatedData: surgeryInfo,
      aiEnhanced: true
    });
  }

  // 新しい手順書通知
  static async notifyNewProcedure(recipientIds, procedureName, senderId) {
    return await this.createBulkNotification({
      recipientIds,
      title: "新しい手順書が追加されました",
      message: `${procedureName}の手順書を確認してください`,
      type: "procedure",
      actionUrl: "/procedures",
      priority: "normal",
      senderId,
      relatedData: { procedureName },
      aiEnhanced: true
    });
  }

  // 緊急連絡
  static async notifyUrgent(recipientIds, message, senderId) {
    return await this.createBulkNotification({
      recipientIds,
      title: "緊急連絡",
      message,
      type: "urgent",
      priority: "urgent",
      senderId,
      aiEnhanced: false  // 緊急時は即座配信
    });
  }

  // システムメンテナンス通知
  static async notifyMaintenance(maintenanceInfo) {
    return await this.createGlobalNotification({
      title: "システムメンテナンスのお知らせ",
      message: `${maintenanceInfo.date} ${maintenanceInfo.time}よりメンテナンスを実施します`,
      type: "system",
      priority: "normal",
      relatedData: maintenanceInfo,
      aiEnhanced: true
    });
  }

  // ========== 新しいAI強化機能 ==========

  // 在庫予測アラート
  static async notifyInventoryPrediction(itemId, prediction, recipientIds = ['inventory_manager']) {
    return await this.createBulkNotification({
      recipientIds,
      title: "在庫予測アラート",
      message: `${prediction.itemName}: ${prediction.daysUntilDepletion}日後に枯渇予測`,
      type: "prediction",
      actionUrl: "/inventory",
      priority: prediction.daysUntilDepletion <= 3 ? "high" : "normal",
      relatedData: { itemId, prediction },
      aiEnhanced: true
    });
  }

  // AI最適化提案
  static async notifyOptimization(suggestion, recipientIds) {
    return await this.createBulkNotification({
      recipientIds,
      title: "AI最適化提案",
      message: suggestion.summary,
      type: "ai_optimization",
      actionUrl: suggestion.actionUrl,
      priority: "normal",
      relatedData: suggestion,
      aiEnhanced: true
    });
  }

  // 緊急AI対応
  static async notifyEmergencyAI(situation, recommendations, recipientIds = ['all']) {
    return await this.createBulkNotification({
      recipientIds,
      title: "緊急事態AI対応ガイド",
      message: `緊急事態: ${situation}`,
      type: "emergency_ai",
      priority: "urgent",
      relatedData: { situation, recommendations },
      aiEnhanced: false  // 緊急時は即座配信
    });
  }

  // ========== AI分析機能 ==========

  static async analyzeWithAI(title, message, type, relatedData) {
    try {
      const ai = await initializeAI();
      if (!ai) return { enhancedMessage: message, analysis: null, recommendedActions: [] };

      const cacheKey = this.hashString(title + message + type);
      if (this.aiCache.has(cacheKey)) {
        return this.aiCache.get(cacheKey);
      }

      const prompt = `
手術室管理の専門家として、以下の通知を分析してください。

タイトル: ${title}
内容: ${message}
タイプ: ${type}

以下を提供してください：
1. より明確なメッセージ（元のメッセージが不明確な場合のみ）
2. 受信者が取るべき具体的行動（3つ以内）
3. 注意事項

簡潔で実用的な回答をお願いします。
`;

      const result = await ai.generateContent(prompt);
      const response = result.response.text();

      const analysis = {
        enhancedMessage: this.extractEnhancedMessage(response, message),
        analysis: response,
        recommendedActions: this.extractRecommendedActions(response),
        confidence: 85
      };

      // 高信頼度の場合キャッシュ（5分間）
      if (analysis.confidence > 80) {
        this.aiCache.set(cacheKey, analysis);
        setTimeout(() => this.aiCache.delete(cacheKey), 300000);
      }

      return analysis;
    } catch (error) {
      console.error('AI分析エラー:', error);
      return { enhancedMessage: message, analysis: null, recommendedActions: [] };
    }
  }

  // ========== WebSocket機能 ==========

  static addWebSocketConnection(userId, socket) {
    this.activeConnections.set(userId, socket);
    console.log(`📡 WebSocket接続追加: ${userId}`);
  }

  static broadcastToWebSocket(notification, notificationId) {
    try {
      const data = {
        id: notificationId,
        ...notification,
        timestamp: new Date().toISOString()
      };

      // 特定ユーザーへの配信
      if (notification.recipientId !== 'all') {
        const socket = this.activeConnections.get(notification.recipientId);
        if (socket && socket.readyState === 1) {
          socket.send(JSON.stringify({
            type: 'notification',
            data
          }));
        }
      }

      // 緊急時は全員に配信
      if (notification.priority === 'urgent') {
        for (const [userId, socket] of this.activeConnections) {
          if (socket.readyState === 1) {
            socket.send(JSON.stringify({
              type: 'urgent_notification',
              data
            }));
          }
        }
      }
    } catch (error) {
      console.error('WebSocket配信エラー:', error);
    }
  }

  // ========== ユーティリティメソッド ==========

  static calculateUrgencyScore(priority, type) {
    const priorityScores = { low: 1, normal: 2, high: 3, urgent: 4, critical: 5 };
    const typeScores = { system: 1, shift: 2, assignment: 3, urgent: 4, emergency_ai: 5 };
    
    return (priorityScores[priority] || 2) + (typeScores[type] || 1);
  }

  static extractRecommendedActions(response) {
    const actions = [];
    if (response.includes('発注')) actions.push('発注申請');
    if (response.includes('確認')) actions.push('詳細確認');
    if (response.includes('連絡')) actions.push('関係者連絡');
    return actions;
  }

  static extractEnhancedMessage(response, originalMessage) {
    // AI応答から改善されたメッセージを抽出
    // 簡易版：元のメッセージをそのまま返す
    return originalMessage;
  }

  static hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }
}